import type { DocPage } from '../types'

export const fileFormats: DocPage = {
  id: 'file-formats',
  title: 'File formats',
  description:
    'Field-level options for GeoPackage, GeoJSON, Shapefile, KML, GeoParquet, OGC API Features, MGDM/Interlis, and X-Format.',
  category: 'Formats',
  sections: [
    { id: 'geopackage', title: 'GeoPackage' },
    { id: 'geojson', title: 'GeoJSON' },
    { id: 'shapefile', title: 'Shapefile' },
    { id: 'kml', title: 'KML' },
    { id: 'geoparquet', title: 'GeoParquet' },
    { id: 'ogc', title: 'OGC API Features' },
    { id: 'mgdm', title: 'MGDM Pipeline / Interlis' },
    { id: 'xformat', title: 'X-Format' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'geopackage',
      text: 'GeoPackage',
    },
    {
      type: 'paragraph',
      text: 'Catalog label: Geopackage. Source and Target for .gpkg files. Supports logical collections by Collection name. Map overview is not the primary interaction; use the model tree and filters.',
    },
    {
      type: 'table',
      headers: ['Field / option', 'Description'],
      rows: [
        ['File path', 'Pick the .gpkg file for source or target.'],
        ['Override Existing', 'Allow replacing existing layers/tables when writing.'],
        ['Filters', 'Limit collections/attributes loaded into the model.'],
        ['Logical collections', 'Add logical layers with Collection name + attributes.'],
        ['Style export', 'QML, SLD, XStyles, and Write QGIS layer_styles inside the GeoPackage — see Styles.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'geojson',
      text: 'GeoJSON',
    },
    {
      type: 'table',
      headers: ['Field / option', 'Description'],
      rows: [
        ['Files', 'Select one or more .geojson / .json files.'],
        ['Folder', 'Point at a directory of GeoJSON files.'],
        ['ZIP', 'Select a .zip archive containing GeoJSON.'],
        ['Include subfolders', 'When picking a folder/archive, include nested paths if offered.'],
        ['Override Existing', 'Target overwrite behavior when writing.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'shapefile',
      text: 'Shapefile',
    },
    {
      type: 'table',
      headers: ['Field / option', 'Description'],
      rows: [
        ['.shp file', 'Select a shapefile (sidecars .dbf/.shx/.prj expected alongside).'],
        ['Folder', 'Directory containing one or more shapefile sets.'],
        ['ZIP', 'Zipped shapefile package.'],
        ['Include subfolders', 'Recurse when the form offers it.'],
        ['Override Existing', 'Target overwrite behavior.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'kml',
      text: 'KML',
    },
    {
      type: 'table',
      headers: ['Field / option', 'Description'],
      rows: [
        ['.kml / .kmz', 'Source or target KML/KMZ path.'],
        ['Folder / ZIP', 'Multi-file pick patterns where offered.'],
        ['KML style import', 'Optional map of Style / StyleMap → symbolizers.'],
        ['Override Existing', 'Target overwrite behavior.'],
      ],
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Styles',
      text: 'KML style import details are also summarized under Styles import & export.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'geoparquet',
      text: 'GeoParquet',
    },
    {
      type: 'table',
      headers: ['Field / option', 'Description'],
      rows: [
        ['.parquet file', 'Source or target GeoParquet path.'],
        ['Override Existing', 'Target overwrite when writing a file that already exists.'],
        ['Filters', 'Limit collections/attributes after model load when offered.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'ogc',
      text: 'OGC API Features',
    },
    {
      type: 'paragraph',
      text: 'Source-only. Provide the service root URL for an OGC API Features endpoint that exposes collections (typically a root that resolves /collections).',
    },
    {
      type: 'table',
      headers: ['Field / option', 'Description'],
      rows: [
        ['Service URL', 'OGC API Features landing/root URL.'],
        ['Filters', 'Limit which remote collections/properties load into the model.'],
        ['Submit / Cancel', 'Connect or dismiss.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'mgdm',
      text: 'MGDM Pipeline / Interlis',
    },
    {
      type: 'paragraph',
      text: 'Shown in the catalog as MGDM Pipeline (interlis_demo). Primarily a Target flow working with Interlis .xtf.',
    },
    {
      type: 'table',
      headers: ['Field / option', 'Description'],
      rows: [
        ['Model Type', 'Standard or Extended.'],
        ['.xtf path', 'Interlis transfer file path for the target pipeline.'],
        ['Override Existing', 'Overwrite behavior when applicable.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'xformat',
      text: 'X-Format',
    },
    {
      type: 'paragraph',
      text: 'Native SEPM interchange using .xf (XFormat JSON) and .tm (TranslatorModel). Anchor on an .xf or .tm file.',
    },
    {
      type: 'table',
      headers: ['Field / option', 'Description'],
      rows: [
        ['Anchor file', 'Pick .xf or .tm as the connection anchor.'],
        ['Write XFormat (.xf)', 'Emit .xf on write.'],
        ['Write TranslatorModel (.tm)', 'Emit .tm on write.'],
        ['Pretty-print JSON', 'Format JSON output for readability.'],
        ['Override Existing', 'Overwrite existing outputs when enabled.'],
      ],
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Scratch to X-Format',
      text: 'Scratch transfers to X-Format always use the scratch write path beside the anchor filename. You do not map into a pre-existing target tree the way you would for PostGIS mapped mode.',
    },
  ],
}
